import { claims } from './claims';
import { bankid } from './bankid';
import { configuration } from './configuration';
import { settings } from './../settings';
import { tools } from './../helpers/tools';
import ajax from '../api/ajax';
import { Filter, OpusResponse } from './models';

/**
 * Opus API and Settings
 */
class Opus {
    constructor() {
        this.url = {
            domain: null,
            path: '',
        };

        this.clinics = [];
        this.clinicians = [];
        this.treatments = [];

        this.filter = new Filter();
    }

    init() {
        this.url.domain = settings.settings.acf[`opus_api_url`];

        claims.init();
        bankid.init();
        configuration.init();
    }

    getPatientStatus() {
        const self = this;

        if (!tools.validateApi(self)) {
            return Promise.reject();
        }

        return new Promise((resolve, reject) => {
            claims
                .get()
                .then((claim) => {
                    if (!claim.result) {
                        reject();

                        return;
                    }

                    const url = `/patient/me`;

                    return ajax
                        .get(self, url, true)
                        .then(() => {
                            resolve(new OpusResponse(false));
                        })
                        .catch(() => {
                            resolve(new OpusResponse(true));
                        });
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    /**
     * Send a listing email.
     * Requires user to be authed and
     * have a valid claim.
     * @param {ListingInfo} listing
     * @return {Promise<OpusResponse>} promise
     */
    sendListing(listing) {
        const self = this;

        if (!tools.validateApi(self)) {
            return Promise.reject();
        }

        return new Promise((resolve, reject) => {
            claims
                .get()
                .then((claim) => {
                    if (!claim.result) {
                        reject();

                        return;
                    }

                    const url = `listing`;

                    return ajax
                        .post(self, url, true, JSON.stringify(listing))
                        .then((result) => {
                            resolve(new OpusResponse(result));
                        })
                        .catch((error) => {
                            const response = new OpusResponse().failed(error, url);

                            tools.handleError(self, response);

                            reject(response);
                        });
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    /**
     * Get all bookings for authed user.
     * Requires user to be authed and
     * have a valid claim.
     * @return {Promise<OpusResponse>} promise
     */
    getBookings() {
        const self = this;

        return new Promise((resolve, reject) => {
            claims
                .get()
                .then((claim) => {
                    if (!claim.result) {
                        reject();

                        return;
                    }

                    const url = `booking?includePastBookings=true`;

                    return ajax
                        .get(self, url, true)
                        .then((result) => {
                            result.sort((a, b) => {
                                return tools.toDate(a.timeSlot.start) - tools.toDate(b.timeSlot.start);
                            });

                            resolve(new OpusResponse(result));
                        })
                        .catch((error) => {
                            const response = new OpusResponse().failed(error, url);

                            tools.handleError(self, response);

                            reject(response);
                        });
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    /**
     * Create a new booking.
     * @param {Booking} booking
     * @return {Promise<OpusResponse>} promise
     */
    createBooking(booking) {
        const self = this;

        return new Promise((resolve, reject) => {
            const url = `booking`;

            return ajax
                .post(self, url, true, JSON.stringify(booking))
                .then((result) => {
                    resolve(new OpusResponse(result));
                })
                .catch((error) => {
                    const response = new OpusResponse().failed(error, url);

                    tools.handleError(self, response);

                    reject(response);
                });
        });
    }

    /**
     * Reschedule a booking.
     * Requires user to be authed and
     * have a valid claim.
     * @param {BookingReschedule} bookingReschedule
     * @return {Promise<OpusResponse>} promise
     */
    rescheduleBooking(bookingReschedule) {
        const self = this;

        if (!tools.validateApi(self)) {
            return Promise.reject();
        }

        return new Promise((resolve, reject) => {
            claims
                .get()
                .then((claim) => {
                    if (!claim.result) {
                        reject();

                        return;
                    }

                    const url = `booking?originalBookingId=${bookingReschedule.originalBookingId}`;

                    return ajax
                        .put(self, url, true, JSON.stringify(bookingReschedule))
                        .then((result) => {
                            resolve(new OpusResponse(result));
                        })
                        .catch((error) => {
                            const response = new OpusResponse().failed(error, url, 'reschedule_failed');

                            tools.handleError(self, response);

                            reject(response);
                        });
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    /**
     * Cancel a booking having the specified id and
     * belonging to the specified clinicId.
     * Requires user to be authed and
     * have a valid claim.
     * @param {number} id
     * @param {string} clinicId
     * @return {Promise<OpusResponse>} promise
     */
    cancelBooking(id, clinicId) {
        const self = this;

        return new Promise((resolve, reject) => {
            claims
                .get()
                .then((claim) => {
                    if (!claim.result) {
                        reject();

                        return;
                    }

                    const url = `booking`;

                    return ajax
                        .delete(
                            self,
                            url,
                            true,
                            JSON.stringify({
                                notifyPatient: true,
                                bookingId: id,
                                clinicId: clinicId,
                            })
                        )
                        .then((result) => {
                            resolve(new OpusResponse(result));
                        })
                        .catch((error) => {
                            const response = new OpusResponse().failed(error, url, 'cancellation_failed');

                            tools.handleError(self, response);

                            reject(response);
                        });
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    /**
     * Get all clinics.
     * @param {boolean} forceRefresh
     * @return {Promise<OpusResponse>} promise
     */
    getClinics(forceRefresh = false) {
        const self = this;

        return new Promise((resolve, reject) => {
            if (!forceRefresh && self.clinics.length > 0) {
                resolve(new OpusResponse(self.clinics));

                return;
            }

            const url = `clinic`;

            return ajax
                .get(self, url)
                .then((result) => {
                    self.clinics = result;

                    if (self.clinics) {
                        self.clinics = self.clinics.sort((a, b) => {
                            return a.name < b.name ? -1 : a.name < b.name ? 1 : 0;
                        });
                    }

                    resolve(new OpusResponse(self.clinics));
                })
                .catch((error) => {
                    const response = new OpusResponse().failed(error, url);

                    tools.handleError(self, response);

                    reject(response);
                });
        });
    }

    /**
     * Get all clinicians for a specific clinic
     * and a specific treatment id.
     * @param {string} clinicId
     * @param {number} treatmentId
     * @return {Promise<OpusResponse>} promise
     */
    getClinicians(clinicId, treatmentId) {
        const self = this;

        return new Promise((resolve, reject) => {
            const url = `clinician?clinicId=${clinicId}${treatmentId ? '&treatmentId=' + treatmentId : ''}`;

            return ajax
                .get(self, url)
                .then((result) => {
                    self.clinicians = result.sort((a, b) => {
                        return a.name < b.name ? -1 : a.name < b.name ? 1 : 0;
                    });

                    resolve(new OpusResponse(self.clinicians));
                })
                .catch((error) => {
                    const response = new OpusResponse().failed(error, url);

                    tools.handleError(self, response);

                    reject(response);
                });
        });
    }

    /**
     * Get all timeslots for a specific clinic within a
     * specific date range and for a specific treatment.
     * Will split timeslots into same length as treatment
     * duration.
     * With optional clinician filtering.
     * @param {string} clinicId
     * @param {number} year
     * @param {number} month
     * @param {number} treatmentId
     * @param {number} duration - Time slot length in minutes
     * @param {number} clinicianId - Optional value
     * @return {Promise<OpusResponse>} promise
     */
    getTimeSlots(clinicId, year, month, treatmentId, duration, clinicianId) {
        const self = this;

        return new Promise((resolve, reject) => {
            const url =
                `timeslot/month?clinicId=${clinicId ? clinicId : ''}&year=${year}&month=${month}&treatmentId=${treatmentId}` +
                `${clinicianId ? '&clinicianId=' + clinicianId : ''}`;

            return ajax
                .get(self, url)
                .then((result) => {
                    resolve(new OpusResponse(result));
                })
                .catch((error) => {
                    const response = new OpusResponse().failed(error, url);

                    tools.handleError(self, response);

                    reject(response);
                });
        });
    }

    /**
     * Get all timeslots for a specific clinic within a
     * specific date range and for a specific treatment.
     * With optional clinician filtering.
     * @param {string} clinicId
     * @param {date} from
     * @param {date} to
     * @param {number} treatmentId
     * @param {number} duration - Time slot length in minutes
     * @param {number} clinicianId - Optional value
     * @return {Promise<OpusResponse>} promise
     */
    getTimeSlotsForDateRange(clinicId, from, to, treatmentId, duration, clinicianId) {
        const self = this;
        const months = [];
        const start = new Date(from.getTime());

        while (start <= to) {
            const key = `${start.getFullYear()},${start.getMonth() + 1}`;

            if ($.inArray(key, months) == -1) months.push(key);

            start.setDate(start.getDate() + 1);
        }

        let sequence = Promise.resolve(new OpusResponse([]));

        months.forEach((month) => {
            sequence = sequence.then((opusResponse) => {
                return self
                    .getTimeSlots(clinicId, month.split(',')[0], month.split(',')[1], treatmentId, duration, clinicianId)
                    .then((response) => {
                        response.result = response.result.filter((timeSlot) => {
                            return (
                                tools.toDate(timeSlot.start) > from &&
                                tools.toDate(timeSlot.start) < to &&
                                !tools.isInPast(timeSlot.start)
                            );
                        });

                        opusResponse.result = opusResponse.result.concat(response.result);
                        opusResponse.result.sort((a, b) => {
                            return new Date(a.start) - new Date(b.start);
                        });

                        return opusResponse;
                    });
            });
        });

        return sequence;
    }

    /**
     * Get all timeslots for a specific city clinics within a
     * specific date range and for a specific treatment.
     * With optional clinician filtering.
     * @param {Array} clinicIdsForSameCity
     * @param {date} from
     * @param {date} to
     * @param {number} treatmentId
     * @param {number} duration - Time slot length in minutes
     * @param {number} clinicianId - Optional value
     * @return {Promise<OpusResponse>} promise
     */
    getTimeSlotsForDateRangeAndCityClinics(clinicIdsForSameCity, from, to, treatmentId, duration, clinicianId) {
        const self = this;
        // const months = [];
        // const start = new Date(from.getTime());

        // while (start <= to) {
        //     const key = `${start.getFullYear()},${start.getMonth() + 1}`;

        //     if ($.inArray(key, months) == -1) months.push(key);

        //     start.setDate(start.getDate() + 1);
        // }

        let sequence = Promise.resolve(new OpusResponse([]));

        clinicIdsForSameCity.forEach((clinicId) => {
            sequence = sequence.then((opusResponse) => {
                if (opusResponse.result.length === 0) {
                    return self
                        .getTimeSlotsForDateRange(clinicId, from, to, treatmentId, duration, clinicianId)
                        .then((response) => {
                            opusResponse.result = opusResponse.result.concat(response.result);
                            return opusResponse;
                        });
                } else {
                    return Promise.resolve(opusResponse);
                }
            });
        });

        return sequence;
    }

    /**
     * Get all timeslots for a list of dates and having
     * one of specified treatments. Filtering on
     * maximum date and also max number of items.
     * @param {array} dates
     * @param {array} treatments
     * @param {date} maxDate
     * @param {number} maxNoOfItems
     * @return {Promise<OpusResponse>} promise
     */
    getTimeSlotsForDays(dates, treatments, maxDate, maxNoOfItems, callback) {
        const self = this;

        let sequence = Promise.resolve(new OpusResponse([]));
        let previousDate = dates[0];

        if (!maxNoOfItems) maxNoOfItems = 1000;

        dates.forEach((date) => {
            treatments.forEach((treatment) => {
                sequence = sequence.then((opusResponse) => {
                    if (previousDate != date && opusResponse.result.length >= maxNoOfItems) return opusResponse;

                    return self.getTimeSlotsForDay(date, treatment, maxDate).then((response) => {
                        previousDate = date;

                        opusResponse.result = opusResponse.result.concat(response.result);
                        opusResponse.result.sort((a, b) => {
                            return new Date(a.start) - new Date(b.start);
                        });

                        if (typeof callback === 'function') {
                            callback(opusResponse);
                        }

                        return opusResponse;
                    });
                });
            });
        });

        return sequence;
    }

    /**
     * Get all timeslots for a date having specified treatment.
     * Filtering on maximum date.
     * @param {date} date
     * @param {Treatment} treatment
     * @param {date} maxDate
     * @return {Promise<OpusResponse>} promise
     */
    getTimeSlotsForDay(date, treatment, maxDate) {
        const self = this;

        return new Promise((resolve, reject) => {
            const url = `timeslot/date?startingFromDate=${tools.formatToISODate(date)}&treatmentId=${treatment.id}`;

            return ajax
                .get(self, url)
                .then((result) => {
                    result = result.filter((timeSlot) => {
                        if (tools.isInPast(timeSlot.start)) {
                            return false;
                        }

                        if (!maxDate || tools.toDate(timeSlot.start) > maxDate) {
                            return false;
                        }

                        const len = tools.getDuration(timeSlot.start, timeSlot.end);

                        return treatment.duration == len;
                    });

                    resolve(new OpusResponse(result));
                })
                .catch((error) => {
                    const response = new OpusResponse().failed(error, url);

                    tools.handleError(self, response);

                    reject(response);
                });
        });
    }

    /**
     * Get all treatments.
     * @param {boolean} forceRefresh
     * @return {Promise<OpusResponse>} promise
     */
    getTreatments(forceRefresh = false) {
        const self = this;

        return new Promise((resolve, reject) => {
            if (!forceRefresh && self.treatments.length > 0) {
                resolve(self.treatments);
            } else {
                return ajax
                    .get(self, 'treatment')
                    .then((result) => {
                        self.treatments = result;

                        resolve(new OpusResponse(self.treatments));
                    })
                    .catch((error) => {
                        const response = new OpusResponse().failed(error);

                        tools.handleError(this, response);

                        reject(response);
                    });
            }
        });
    }

    /**
     * Check if time is available for reservation.
     * @param {string} clinicId
     * @param {TimeSlot} timeSlot
     * @return {Promise<OpusResponse>} promise
     */
    checkReservation(timeSlot) {
        const self = this;

        return new Promise((resolve, reject) => {
            const url = `reservation?clinicId=${timeSlot.clinicId}&clinicianId=${timeSlot.clinicianId
                }&start=${timeSlot.start.replace('Z', '')}&end=${timeSlot.end.replace('Z', '')}${timeSlot.treatmentId ? '&treatmentId=' + timeSlot.treatmentId : ''
                }`;

            return ajax
                .get(self, url)
                .then((result) => {
                    resolve(new OpusResponse(result));
                })
                .catch((error) => {
                    const response = new OpusResponse().failed(error, url);

                    tools.handleError(self, response);

                    reject(response);
                });
        });
    }

    /**
     * Create a reservation.
     * @param {number} id
     * @param {TimeSlot} timeSlot
     * @return {Promise<OpusResponse>} promise
     */
    createReservation(timeSlot) {
        const self = this;

        return new Promise((resolve, reject) => {
            const url =
                `reservation?id=${timeSlot.id}&clinicId=${timeSlot.clinicId}&clinicianId=${timeSlot.clinicianId}` +
                `&start=${timeSlot.start.replace('Z', '')}&end=${timeSlot.end.replace('Z', '')}${timeSlot.treatmentId ? '&treatmentId=' + timeSlot.treatmentId : ''
                }`;

            return ajax
                .post(self, url)
                .then((result) => {
                    resolve(new OpusResponse(result));
                })
                .catch((error) => {
                    const response = new OpusResponse().failed(error, url);

                    tools.handleError(self, response);

                    reject(response);
                });
        });
    }

    /**
     * Delete a reservation.
     * @param {number} id
     * @return {Promise<OpusResponse>} promise
     */
    deleteReservation(id) {
        const self = this;

        return new Promise((resolve, reject) => {
            const url = `reservation?reservationId=${id}`;

            return ajax
                .delete(self, url)
                .then((result) => {
                    resolve(new OpusResponse(result));
                })
                .catch((error) => {
                    const response = new OpusResponse().failed(error, url);

                    tools.handleError(self, response);

                    reject(response);
                });
        });
    }

    /**
     * Save health declaration.
     * @param {Health} health
     * @return {Promise<OpusResponse>} promise
     */
    saveHealth(health) {
        const self = this;

        if (!tools.validateApi(self)) {
            return Promise.reject();
        }

        return new Promise((resolve, reject) => {
            claims
                .get()
                .then((claim) => {
                    if (!claim.result) {
                        reject();

                        return;
                    }

                    const url = `health`;

                    return ajax
                        .put(self, url, true, JSON.stringify(health))
                        .then((result) => {
                            resolve(new OpusResponse(result));
                        })
                        .catch((error) => {
                            const response = new OpusResponse().failed(error, url);

                            tools.handleError(self, response);

                            reject(response);
                        });
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    /**
     * Get health declaration information.
     * @return {Promise<OpusResponse>} promise
     */
    getHealth() {
        const self = this;

        if (!tools.validateApi(self)) {
            return Promise.reject();
        }

        return new Promise((resolve, reject) => {
            claims
                .get()
                .then((claim) => {
                    if (!claim.result) {
                        reject();

                        return;
                    }

                    const url = `health`;

                    return ajax
                        .get(self, url, true)
                        .then((result) => {
                            resolve(new OpusResponse(result));
                        })
                        .catch((error) => {
                            if (error && error.status === 500) {
                                // Service returns 500 if no health declaration is available
                                resolve(new OpusResponse(null));
                            } else {
                                const response = new OpusResponse().failed(error, url);

                                tools.handleError(self, response);

                                reject(response);
                            }
                        });
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    /**
     * Find a timeslot in an array of timeslots.
     * @param {array} timeSlots
     * @param {number} id
     * @param {string} clinicId
     * @param {string} start
     * @return {TimeSlot} timeSlot
     */
    findTimeSlot(timeSlots, id, clinicId, start) {
        for (let i = 0; i < timeSlots.length; i++) {
            const t = timeSlots[i];

            if (t.id == id && t.start == start && t.clinicId == clinicId) {
                return t;
            }
        }

        return null;
    }
}

export let opus = new Opus();



// WEBPACK FOOTER //
// ./scripts/booking/api/opus.js